import { computed } from "vue";

/**
 * Table configuration for OperatingCountryTable.
 *
 * Columns: Operating Country · Country Currency · Work hours per year ·
 * Status · Chatter · Menu. The Default badge is inline in the country cell.
 *
 * Row actions are surfaced as callbacks so the parent can emit them upward —
 * the shared table stays free of API calls (PR checklist §4/§5).
 *
 * @param {object} handlers
 * @param {(payload: {item: object, value: number}) => void} [handlers.onSetWorkHours]
 * @param {(payload: {item: object, action: string}) => void} [handlers.onMenuAction]
 * @param {(payload: {item: object}) => void} [handlers.onChatterClick]
 */
export function useTableConfig(handlers = {}) {
  const {
    onSetWorkHours = () => {},
    onMenuAction = () => {},
    onChatterClick = () => {},
  } = handlers;

  const isDefault = (item) => item.is_default === true;
  const isRetired = (item) =>
    item.active === false || item.status === "retired";
  const statusValue = (item) =>
    item.status || (item.active === false ? "retired" : "active");

  const countryName = (item) =>
    item.country?.name || item.name || "";
  const countryIso = (item) =>
    item.country?.iso_code ||
    item.iso_code ||
    item.country?.code ||
    "";
  const flagIcon = (item) =>
    item.country?.flag_icon || item.flag_icon || "";
  const currencyCode = (item) =>
    item.country?.currency?.code || item.currency_code || "";

  const tableParams = computed(() => {
    return {
      headers: [
        { text: "Operating Country", sortable: false, value: "name", width: "32%" },
        { text: "Country Currency", sortable: false, value: "currency", width: "16%" },
        {
          text: "Work hours per year",
          sortable: false,
          value: "work_hours_per_year",
          width: "20%",
        },
        { text: "Status", sortable: false, value: "status", width: "14%" },
        { text: "", sortable: false, value: "chatter", width: "9%" },
        { text: "", sortable: false, value: "actions", width: "9%" },
      ],
      columns: [
        {
          field: "name",
          component: "OperatingCountryNameCell",
          props: (item) => ({
            name: countryName(item),
            iso_code: countryIso(item),
            flag_icon: flagIcon(item),
            is_default: isDefault(item),
            retired: isRetired(item),
          }),
          events: () => ({}),
        },
        {
          field: "currency",
          component: "CountryCurrencyCodeBadge",
          props: (item) => ({
            code: currencyCode(item),
          }),
        },
        {
          field: "work_hours_per_year",
          component: "WorkHoursPerYearInput",
          props: (item) => ({
            value: item.work_hours_per_year,
            rowState: isRetired(item) ? "retired" : "active",
            loading: item.work_hours_loading === true,
            error: item.work_hours_error || "",
            disabled: false,
          }),
          events: (item) => ({
            commit: (value) => onSetWorkHours({ item, value }),
          }),
        },
        {
          field: "status",
          component: "Badge",
          props: (item) => ({
            status: statusValue(item),
          }),
        },
        {
          field: "chatter",
          component: "ChatterIcon",
          props: () => ({
            unread: 0,
          }),
          events: (item) => ({
            onClick: () => onChatterClick({ item }),
          }),
        },
        {
          field: "actions",
          component: "OperatingCountryLineMenu",
          props: (item) => ({
            isDefault: isDefault(item),
            status: isRetired(item) ? "Retired" : "Active",
          }),
          events: (item) => ({
            action: (payload) =>
              onMenuAction({ item, action: payload?.type }),
          }),
        },
      ],
      filters: {
        values: [],
        searchText: "",
      },
      hideTopSection: true,
      hideActiveNo: true,
      activeRecords: {
        field: "status",
        value: "active",
      },
      defaultOrderingField: "name",
      activeRecordsCount: 0,
      hideGridView: true,
      filterBtnLabel: "Filter",
      filterBtnFitContent: true,
      actionLabel: "Add Country",
      title: "",
      orderingStorageKey: "operatingCountries.ordering",
      cellContainerMaxWidth: "260px",
      itemsPerPage: 10,
    };
  });

  return {
    tableParams,
  };
}
